import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useParams } from 'react-router-dom';

import {
  getContactDisplayName,
  getContactFullAddress,
} from '@/components/postgrid/common/contactUtils';
import { PostgridBreadcrumbs } from '@/components/postgrid/common/PostgridBreadcrumbs';
import { PostgridSubNavigation } from '@/components/postgrid/common/PostgridSubNavigation';
import { TestModeBanner } from '@/components/postgrid/common/TestModeBanner';
import { EventTimeline } from '@/components/postgrid/events/EventTimeline';
import { Badge } from '@/components/ui/catalyst/badge';
import { Heading } from '@/components/ui/catalyst/heading';
import { Text } from '@/components/ui/catalyst/text';
import {
  type ReadPostgridContact,
  usePostgridContactsRetrieveQuery,
  usePostgridEventsListQuery,
} from '@/services/api/postgridService';

/**
 * ContactDetailPage - Full page view for individual contact details
 * Accessible at /postgrid/contacts/<contact_id>
 */
export const ContactDetailPage: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();

  const {
    data: contact,
    isLoading,
    error,
  } = usePostgridContactsRetrieveQuery(
    { id: contactId || '' },
    {
      skip: !contactId,
    },
  );

  // Fetch events for this contact
  const { data: events = [] } = usePostgridEventsListQuery(
    { objectId: contactId || '' },
    { skip: !contactId },
  );

  if (!contactId) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Contact ID is required</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-zinc-50 min-h-screen">
        <div className="bg-white border-b border-zinc-200 px-6 py-4">
          <div className="space-y-3">
            <PostgridBreadcrumbs />
            <div>
              <Heading className="text-zinc-900 font-semibold">
                Contact Details
              </Heading>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error loading contact details</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-zinc-50 min-h-screen">
        <div className="bg-white border-b border-zinc-200 px-6 py-4">
          <div className="space-y-3">
            <PostgridBreadcrumbs />
            <div>
              <Heading className="text-zinc-900 font-semibold">
                Contact Details
              </Heading>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="bg-white rounded-lg border border-zinc-200 p-6">
            <p className="text-gray-600">Loading contact details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="bg-zinc-50 min-h-screen">
        <div className="bg-white border-b border-zinc-200 px-6 py-4">
          <div className="space-y-3">
            <PostgridBreadcrumbs />
            <div>
              <Heading className="text-zinc-900 font-semibold">
                Contact Details
              </Heading>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="bg-white rounded-lg border border-zinc-200 p-6">
            <p className="text-gray-600">Contact not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200">
        <div className="px-6 py-4">
          <div className="space-y-3">
            <PostgridBreadcrumbs />
            <div>
              <Heading className="text-zinc-900 font-semibold">
                {getContactDisplayName(contact as ReadPostgridContact)}
              </Heading>
            </div>
          </div>
        </div>
        <PostgridSubNavigation className="px-6" />
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="bg-white rounded-lg border border-zinc-200 p-6">
          <div className="space-y-6 max-w-4xl mx-auto">
            {/* Key Information - What paralegals need to see first */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="text-xl font-semibold text-gray-900 mb-2">
                    {getContactDisplayName(contact as ReadPostgridContact)}
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <Badge
                      color={contact.is_verified ? 'green' : 'red'}
                      className="font-medium"
                    >
                      {contact.is_verified ? '✓ Verified' : '⚠ Unverified'}
                    </Badge>
                    <TestModeBanner isTest={!contact.live} variant="compact" />
                  </div>
                </div>
                <div className="text-right text-sm text-gray-600">
                  <div>Last updated</div>
                  <div className="font-medium">
                    {contact.modified_at
                      ? new Date(contact.modified_at).toLocaleDateString()
                      : 'Unknown'}
                  </div>
                </div>
              </div>
            </div>

            {/* Mailing Address - PRIMARY PURPOSE */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Mailing Address
              </h3>
              <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                {getContactFullAddress(contact as ReadPostgridContact) ? (
                  <div className="space-y-2">
                    <div className="text-lg font-semibold text-gray-900">
                      {getContactDisplayName(contact as ReadPostgridContact)}
                    </div>
                    {contact.address_line1 && (
                      <div className="text-gray-800">
                        {contact.address_line1}
                      </div>
                    )}
                    {contact.address_line2 && (
                      <div className="text-gray-600">
                        {contact.address_line2}
                      </div>
                    )}
                    <div className="text-gray-800">
                      {[contact.city, contact.province_or_state]
                        .filter(Boolean)
                        .join(', ')}
                      {contact.postal_or_zip && ` ${contact.postal_or_zip}`}
                    </div>
                    {contact.country && contact.country !== 'CA' && (
                      <div className="text-gray-600">{contact.country}</div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Text className="text-red-600 font-medium">
                      ⚠ No mailing address available
                    </Text>
                    <Text className="text-sm text-gray-500 mt-1">
                      This contact cannot receive mail until an address is added
                    </Text>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Contact Methods */}
            {(contact.phone_number || contact.email) && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Additional Contact Methods
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Phone */}
                  {contact.phone_number && (
                    <div className="bg-white border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Phone</div>
                      <div className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <PhoneIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        {contact.phone_number}
                      </div>
                    </div>
                  )}

                  {/* Email */}
                  {contact.email && (
                    <div className="bg-white border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Email</div>
                      <div className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        {contact.email}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            {contact.notes && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Notes
                </h3>
                <div className="bg-white border rounded-lg p-4">
                  <Text className="whitespace-pre-wrap">{contact.notes}</Text>
                </div>
              </div>
            )}

            {/* Activity Timeline */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Activity Timeline
              </h3>
              <EventTimeline
                events={events}
                emptyMessage="No activity events yet"
              />
            </div>

            {/* Technical Details - Collapsed by default */}
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center gap-2">
                <span className="transition-transform group-open:rotate-90">
                  ▶
                </span>
                Technical Details
              </summary>
              <div className="mt-3 space-y-4 pl-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-medium text-gray-500">PostGrid ID</div>
                    <div className="mt-1 font-mono">{contact.postgrid_id}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-500">Contact ID</div>
                    <div className="mt-1 font-mono">{contact.id}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-500">Created</div>
                    <div className="mt-1">
                      {contact.created_at
                        ? new Date(contact.created_at).toLocaleDateString()
                        : 'Unknown'}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-500">
                      Last Modified
                    </div>
                    <div className="mt-1">
                      {contact.modified_at
                        ? new Date(contact.modified_at).toLocaleDateString()
                        : 'Unknown'}
                    </div>
                  </div>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailPage;
